<script lang="ts" setup>
import { useMate } from "@/stores/mate";
import { useRouteParams } from "@vueuse/router";
import { computed, onBeforeMount, ref, watch } from "vue";
import { routerBack } from "@/use/go";
import {
  createPrivateSession,
  startChatSessionByPopup,
} from "@/components/mobile/chat/chatUtils";
import { useGlobalUser } from "@/stores/globalUser";
import {
  getDefaultPersonaId,
  replacePlaceHolder,
} from "@/components/mobile/chat/mode/util";
import { marked } from "marked";
import { useUI } from "@/stores/ui";
import { usePopular } from "@/stores/popular";
import { useRoute } from "vue-router";
import { copyToClipboard } from "@/modules/common";
import { showSuccessToast } from "vant";
import i18n from "@/locales";
import { ChatScene } from "@/types/chat";
import { Mate } from "@/types/entity";
import { useMateChatHead } from "@/composable/head/useMateChatHead";
import { useBrowserLocation } from "@vueuse/core";

const mateStore = useMate();
const userStore = useGlobalUser();
const uiStore = useUI();
const popularStore = usePopular();
const location = useBrowserLocation();
const uuid = useRouteParams<string>("uuid");
const mateName = useRouteParams<string>("name");
const mate = ref<Mate | null>(null);
const loading = ref(false);
const route = useRoute();
const backgroundImage = computed(() => `url(${mate.value?.mediaUrl})`);
const isSeoRoute = route.path.includes("nsfw-ai");
const routeName = "mateChatIntro";
const defaultScene = computed<ChatScene | null>(() => {
  if (!mate.value?.defaultChatSceneId) return null;
  if (mate.value?.defaultChatScene) {
    return mate.value.defaultChatScene;
  } else if (mate.value?.chatScenezz?.length) {
    return (
      mate.value.chatScenezz.find(
        (s) => s.id === mate.value?.defaultChatSceneId
      ) ?? null
    );
  } else {
    return null;
  }
});

useMateChatHead(mate);

// Lifecycle
onBeforeMount(async () => {
  try {
    loading.value = true;
    mate.value = await mateStore.getOrFetchMate(uuid.value);
    popularStore.getOrFetchFeaturedMateList();
  } finally {
    loading.value = false;
  }
});

watch(uuid, async () => {
  if (route.name !== routeName) return;
  loading.value = true;
  try {
    mate.value = await mateStore.getOrFetchMate(uuid.value);
  } finally {
    loading.value = false;
  }
});

// Event Handler
const startChatQuickly = async () => {
  // 1. 未登录：弹窗
  if (!userStore.isLogin) {
    await startChatSessionByPopup(mate.value!, true);
    return;
  }
  const uiStore = useUI();
  if (mateStore?.currentMate?.uuid === mate.value!.uuid) {
    uiStore.showAnotherMateSelector = true;
    return;
  }
  // 2. 直接创建session
  const defaultPersonaId = getDefaultPersonaId(mate.value!);
  await createPrivateSession(
    mate.value!.id!,
    mate.value!.uuid,
    "push",
    undefined,
    defaultPersonaId
  );
};

function parsePrompt(txt: string) {
  if (!txt) return "";
  const msg = replacePlaceHolder(
    txt,
    mate.value.name ?? "",
    mateStore.currentMate?.name ?? ""
  );
  // const content = escapeHtml(msg);
  return marked.parse(msg);
}

const filteredFeaturedMates = computed(() =>
  popularStore.featuredMateList.filter((m) => m.uuid !== mate.value?.uuid)
);

const onCopy = async () => {
  copyToClipboard(
    `${location.value.protocol}//${location.value.host}/nsfw-ai/chat/${mateName.value}/${uuid.value}`
  );
  showSuccessToast(i18n.global.t("Message.copySuccessful"));
};
</script>
<template>
  <div class="w-full h-full relative">
    <div
      v-show="mate"
      :style="{
        backgroundImage: backgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }"
      class="w-full h-full z-0 absolute inset-0 pointer-events-none"
    >
      <img
        :src="mate?.mediaUrl"
        alt=""
        class="w-full h-full object-cover lg:object-contain backdrop-blur-xl"
      />
    </div>
    <div
      class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden z-10 relative"
    >
      <ThreadsNav class="bg-transparent" show-back>
        <template #left>
          <button
            class="btn btn-ghost border-0 bg-zinc-800/50 btn-sm btn-circle hover:bg-zinc-800 text-white"
            @click="routerBack"
          >
            <i class="iconify mingcute--left-line text-base" />
          </button>
        </template>
        <template #center>
          <div></div>
        </template>
      </ThreadsNav>
      <div
        v-if="loading"
        class="w-full h-full flex flex-col justify-center items-center"
      >
        <span class="loading" />
      </div>
      <div
        v-else-if="mate"
        class="p-4 flex flex-col lg:flex-row-reverse overflow-hidden gap-2"
      >
        <div
          class="flex flex-col justify-end items-end h-full overflow-hidden gap-2 flex-grow"
        >
          <div
            class="w-full lg:w-[40rem] lg:max-w-[50%] flex flex-col bg-zinc-900/70 backdrop-blur-lg py-4 rounded-lg gap-2 text-white mate-introduction overflow-hidden"
          >
            <h1 class="font-bold text-lg px-4">{{ mate.name }}</h1>
            <h2 class="hidden">
              nsfw, nsfw-ai, crushon,character ai, best nsfw chat
            </h2>
            <h3 class="px-4">
              <MateTagGroup :mate="mate" />
            </h3>
            <div
              class="max-h-[30rem] overflow-y-scroll px-4 flex flex-col gap-2 flex-grow"
            >
              <p>
                {{ mate.bio }}
              </p>
              <div v-if="defaultScene" class="flex flex-col gap-1">
                <span
                  class="font-semibold text-white/60 flex items-center gap-1"
                >
                  <i class="iconify mingcute--book-2-line" />
                  {{ $t("User.mate.chat.scenario") }}
                </span>
                <p v-if="defaultScene?.intro" class="chat-container mb-2">
                  {{ defaultScene?.intro }}
                </p>
                <p
                  v-if="defaultScene?.scenario"
                  class="chat-container"
                  v-html="parsePrompt(defaultScene?.scenario ?? '')"
                />
              </div>
              <div v-if="mate.greeting" class="flex flex-col gap-1">
                <span
                  class="font-semibold text-white/60 flex items-center gap-1"
                >
                  <i class="iconify mingcute--message-2-line" />
                  {{ $t("User.mate.chat.greetingMessage") }}
                </span>
                <p
                  class="chat-container"
                  v-html="parsePrompt(defaultScene?.greeting ?? '')"
                />
              </div>
            </div>
          </div>
          <div
            class="grid grid-cols-[1fr_auto_auto_auto] lg:flex gap-2 w-full overflow-hidden items-end shrink-0"
          >
            <div class="w-full overflow-hidden flex justify-end">
              <button
                v-if="mateStore.currentMate"
                class="dark-btn overflow-hidden max-w-full join-item"
                @click="uiStore.popMateSelector({})"
              >
                <div class="grid grid-cols-[auto_1fr_auto] gap-2 items-center">
                  <UserAvatar
                    :avatar-url="mateStore.currentMate.mediaUrl"
                    :mate="mateStore.currentMate"
                    class="w-5"
                  />
                  <span
                    class="overflow-hidden text-ellipsis text-xs sm:text-base whitespace-nowrap"
                  >
                    {{ mateStore.currentMate?.name }}
                  </span>
                  <i class="iconify mingcute--down-small-line shrink-0" />
                </div>
              </button>
            </div>
            <button class="dark-btn btn-square shrink-0" @click="onCopy">
              <i class="iconify mingcute--share-2-line shrink-0" />
            </button>
            <button
              class="dark-btn btn-square shrink-0"
              @click="startChatSessionByPopup(mate, true)"
            >
              <i class="iconify mingcute--settings-3-line shrink-0" />
            </button>
            <button
              class="btn btn-primary bg-primary-gradient shrink-0"
              @click="startChatQuickly(mate)"
            >
              {{ $t("Buttons.startChat") }}
            </button>
          </div>
        </div>
        <div
          v-if="isSeoRoute"
          class="w-full lg:w-auto lg:h-full overflow-scroll gap-2 flex lg:flex-col no-scrollbar"
        >
          <ChatMateCard
            v-for="m in filteredFeaturedMates"
            :key="m.id"
            :mate="m"
            :show-badge="false"
            :show-count="false"
            class="w-[6rem] shrink-0"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.dark-btn {
  @apply btn btn-sm sm:btn-md btn-ghost bg-zinc-800/60 hover:bg-zinc-800 backdrop-blur text-white;
}
</style>
